import type { ReactElement, CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import classnames from "classnames";

import LogoNav from "common/app_frame/logo_nav";
import { FullLogo } from "common/core/logo";
import { useScreenClass } from "common/core/responsive";
import UnauthenticatedAppFrame from "common/app_frame/unauthenticated";
import { EnvironmentWarningHeader } from "common/account/login/components";
import type { OrgBrand } from "common/core/brand/get_org_brand_unauthed";
import { useProofRebrand } from "util/feature_detection";

import NewBrandBanner from "./brand_login_banner";
import Styles from "./index.module.scss";

type Props = {
  children: ReactElement;
  organizationName?: string | null;
  brand?: OrgBrand | null;
};

const Background = ({ children, brand, organizationName }: Props) => {
  const isProof = useProofRebrand();
  const isMobile = useScreenClass() === "xs";
  const { pathname } = useLocation();
  const isCobranded = Boolean(brand?.orgLogo);
  if (isMobile) {
    return (
      <UnauthenticatedAppFrame
        orgLogo={brand?.orgLogo}
        orgName={organizationName}
        header={brand?.header}
      >
        {children}
      </UnauthenticatedAppFrame>
    );
  }

  return (
    <>
      <EnvironmentWarningHeader />
      <NewBrandBanner />
      <div
        className={
          isProof
            ? classnames(Styles.background, !isCobranded && Styles.backgroundGraphic)
            : Styles.deprecatedBackground
        }
        style={{ "--web-theme-color": brand?.webThemeColor } as CSSProperties}
      >
        <div className={Styles.canvas} data-automation-id="account-header-element">
          {isCobranded ? (
            <div className={Styles.containerHeader}>
              <LogoNav
                orgName={organizationName}
                logoUrl={brand?.orgLogo}
                headerStyle={brand?.header}
                readOnly={pathname === "/login"}
              />
            </div>
          ) : (
            <FullLogo whiteLogo className={Styles.logo} />
          )}
          <div className={Styles.loginBody}>{children}</div>
        </div>
        {!isProof && (
          <svg
            className={Styles.svg}
            width="100%"
            viewBox="0 0 1440 200"
            height="35%"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <mask id="proof-background-mask">
              <path
                fill="white"
                d="M 0 730 H 1790 C 1751 787 2134.54 -28.3362 1456.77 33.7314 C 779.006 95.7988 1026.92 -49.0418 -17.7171 33.7314 Z"
              ></path>
            </mask>
            <rect width="100%" height="100%" fill="white" mask="url(#proof-background-mask)" />
          </svg>
        )}
      </div>
    </>
  );
};

export default Background;
